// ** React Imports
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

// ** Third Party Imports
import { ImageIcon } from '@saleshandy/design-system';
import Lottie from 'lottie-react';

// ** Constants (enums, types,images, constants, etc.) Imports
import AiVariant3StarIcon from '../../../../assets/json/lottieanimation/ai-variant-3-star-icon.json';
import { Images } from '../../../../shared/app-constants';
import { RequestStatus } from '../../../../shared/enums/request-status';
import { AiSequenceGeneratorSteps } from './enums';
import { AiSeqGeneratorModalProps, WrapperStepComponentProps } from './type';

// ** Custom Imports
import Modal from '../../../../shared/design-system/components/atoms/modal';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import AiCopilotUserChatCard from './components/ai-copilot-user-chat-card';
import AiLoadingAnimationCard from './components/ai-loading-animation-card/ai-loading-animation-card';
import AI_SEQ_STEP_COMPONENT_MAP from './components/ai-seq-steps-list';
import AiErrorRespond from './components/ai-seq-steps-list/ai-error-respond/ai-error-respond';
import AiSeqCloseConfirmationModal from './components/ai-seq-steps-list/ai-seq-close-confirmation-modal/ai-seq-close-confirmation-modal';
import OnBoardingStep from './components/ai-seq-steps-list/on-boarding-step';

/**
 * @description Wrapper Step Component for the AI Sequence Generator Modal
 * @param {WrapperStepComponentProps} props - Props for the Wrapper Step Component
 * @returns => React Component
 */
export const WrapperStepComponent: React.FC<WrapperStepComponentProps> = memo(
  ({
    currentStep,
    handleModalClose,
    addChatMessage,
    aiSeqGeneratorStatus,
    saveCurrentStepDetails,
    currentStepPayload,
    saveAiSeqGeneratorStepDetailsRequestStatus,
    regeneratedStep = null,
  }) => {
    // ** Memoized Dynamic Step Component
    const StepComponent = useMemo(
      () => AI_SEQ_STEP_COMPONENT_MAP?.[currentStep] || null,
      [currentStep],
    );

    // ** Render Loading Animation Card Component
    if (
      aiSeqGeneratorStatus === RequestStatus.Pending &&
      !regeneratedStep &&
      currentStep !== AiSequenceGeneratorSteps.SEQUENCE_GENERATION
    ) {
      return <AiLoadingAnimationCard currentStep={currentStep} />;
    }

    // ** Render Error Respond Component
    if (aiSeqGeneratorStatus === RequestStatus.Failed) {
      return (
        <AiErrorRespond
          saveCurrentStepDetails={saveCurrentStepDetails}
          currentStepPayload={currentStepPayload}
          saveAiSeqGeneratorStepDetailsRequestStatus={
            saveAiSeqGeneratorStepDetailsRequestStatus
          }
        />
      );
    }

    return (
      StepComponent && (
        // ** Render Step Component
        <StepComponent
          handleMoveToNextStep={() => {}}
          handleModalClose={handleModalClose}
          addChatMessage={addChatMessage}
          saveCurrentStepDetails={saveCurrentStepDetails}
        />
      )
    );
  },
);

/**
 * @description AI Sequence Generator Modal, Modal for the AI Sequence Generator each step
 * @param {AiSeqGeneratorModalProps} props - Props for the AI Sequence Generator Modal
 * @returns => React Modal Component based on condition
 */
const AiSeqGeneratorModal: React.FC<AiSeqGeneratorModalProps> = ({
  isOpen = false,
  currentStep = AiSequenceGeneratorSteps.INITIATE_AI_SEQUENCE,
  chatConversation = [],
  aiSeqGeneratorStatus = RequestStatus.Ideal,
  handleModalClose = () => {},
  addChatMessage = () => {},
  discardSession = () => {},
  saveAiSeqGeneratorStepDetailsRequestStatus,
  saveCurrentStepDetails,
  currentStepPayload,
  regeneratedStep,
}) => {
  // ** State Variables
  const [animationStage, setAnimationStage] = useState<number>(0);
  const [showStep, setShowStep] = useState<boolean>(false);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  // ** Handlers
  const handleAiSeqModalClose = useCallback(() => {
    setShowCloseModal(true);
  }, [setShowCloseModal]);

  const handleDiscardSequence = useCallback(() => {
    setShowCloseModal(false);
    discardSession();
    handleModalClose();
  }, [handleModalClose, setShowCloseModal, discardSession]);

  const handleResumingProcess = useCallback(() => {
    setShowCloseModal(false);
  }, [handleModalClose, setShowCloseModal]);

  // ** Effects
  // ** Start Animation
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen && currentStep === AiSequenceGeneratorSteps.COMPANY_INFO) {
      setAnimationStage(1);
      timer = setTimeout(() => {
        setShowStep(true);
        setTimeout(() => setAnimationStage(2), 800);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [isOpen, currentStep]);

  // ** Reset Animation
  useEffect(() => {
    if (!isOpen) {
      setAnimationStage(0);
      setShowStep(false);
    }
  }, [isOpen]);

  // ** Show Onboarding Step Modal
  if (isOpen && currentStep === AiSequenceGeneratorSteps.INITIATE_AI_SEQUENCE) {
    return (
      <Modal
        show={isOpen}
        onClose={handleModalClose}
        onHide={() => {}}
        hideHeader
        hideFooter
        className="on-boarding-step-modal"
      >
        <OnBoardingStep
          handleModalClose={handleModalClose}
          addChatMessage={addChatMessage}
        />
      </Modal>
    );
  }

  return (
    <>
      {isOpen && (
        <Modal
          show={isOpen}
          onClose={handleModalClose}
          onHide={() => {}}
          hideHeader
          hideFooter
          className={`ai-seq-generator-modal ${
            animationStage >= 1 ? 'expanded-modal' : ''
          } ${showStep ? 'expanded-modal-sec' : ''}`}
        >
          <div className="ai-seq-generator-modal-container">
            <div className="ai-seq-generator-modal-container__header">
              <div className="ai-seq-generator-modal-container__header-text">
                <Lottie
                  animationData={AiVariant3StarIcon}
                  loop
                  className="ai-3-star-animated-icon"
                />
                AI Sequence Copilot
              </div>
              <span
                className="ai-seq-generator-modal-container__header-icon"
                {...accessibleOnClick(handleAiSeqModalClose)}
              >
                <ImageIcon
                  src={Images.CrossGray}
                  alt="cross-gray"
                  width={16}
                  height={16}
                />
              </span>
            </div>
            <div className="ai-seq-generator-modal-container__content">
              <div
                className={`ai-seq-generator-modal-container__content-chat
              ${!showStep ? 'visible-ai-modal' : ''}`}
              >
                <AiCopilotUserChatCard chatConversation={chatConversation} />
              </div>
              <div
                className={`ai-seq-generator-modal-container__content-steps  ${
                  animationStage >= 2 && showStep ? `visible-steps` : ''
                }`}
              >
                <WrapperStepComponent
                  aiSeqGeneratorStatus={aiSeqGeneratorStatus}
                  currentStep={currentStep}
                  handleModalClose={handleModalClose}
                  addChatMessage={addChatMessage}
                  saveCurrentStepDetails={saveCurrentStepDetails}
                  currentStepPayload={currentStepPayload}
                  saveAiSeqGeneratorStepDetailsRequestStatus={
                    saveAiSeqGeneratorStepDetailsRequestStatus
                  }
                  regeneratedStep={regeneratedStep}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showCloseModal && (
        <AiSeqCloseConfirmationModal
          show={showCloseModal}
          onDiscardSequence={handleDiscardSequence}
          onResumingProcess={handleResumingProcess}
        />
      )}
    </>
  );
};

export default memo(AiSeqGeneratorModal);
